import { createContext } from 'react';

/**
 * Callback function type for sidebar selection changes
 * Called when sidebar closes with the final selected record ID
 */
export type SidebarSelectionCallback = (
  entityId: string,
  recordId: string | undefined,
  groupId: string | undefined,
  recordName: string | undefined
) => void;

/**
 * Context for controlling the form sidebar
 * Uses fully controlled components - provider owns all entity selection state
 */
export interface SidebarControlContextValue {
  /**
   * Opens the sidebar entity tab with specified entity/record and optional callback.
   * `recordIsNew` marks a record the opener just created, so the sidebar mounts it
   * unlocked and ready to be named instead of re-locking it.
   * `onSwitchToManualEntry` is the opener's own mode switch (ReferenceSelect's
   * `toggleMode`), surfaced by the entity tab as "Switch to Manual Entry".
   */
  openEntitySidebar: (
    entityId: string,
    recordId?: string,
    entityGroupId?: string,
    recordName?: string,
    onSelectionChange?: SidebarSelectionCallback,
    recordIsNew?: boolean,
    onSwitchToManualEntry?: () => void
  ) => void;
  /** Opens the sidebar reminders tab */
  openRemindersSidebar: () => void;
  /** Opens the sidebar timeline tab */
  openTimelineSidebar: () => void;
  /** Closes the sidebar */
  closeSidebar: () => void;
  /** Check if sidebar has a callback binding */
  hasBinding: () => boolean;
  /** Clear the callback binding */
  clearBinding: () => void;
  /** Currently selected entity ID in sidebar */
  selectedEntityId: string | undefined;
  /** Currently selected record ID in sidebar */
  selectedRecordId: string | undefined;
  /** Name of the currently selected record in sidebar */
  selectedRecordName: string | undefined;
  /** Currently selected entity group ID in sidebar */
  selectedGroupId: string | undefined;
  /**
   * Id of the record the opener just created, if any. The single source of truth
   * for the sidebar's "new record" state: the record it names mounts unlocked and
   * namable. Keyed by id rather than a flag so selecting any other record cannot
   * inherit it, and cleared when the sidebar closes so a reopen starts locked.
   */
  newRecordId: string | undefined;
  /**
   * The opener's switch back to manual entry, when it supplied one. Read by the
   * entity tab to render its "Switch to Manual Entry" control, so it is state in
   * the provider rather than a ref. Cleared with the rest of the session when the
   * sidebar closes.
   */
  switchToManualEntry: (() => void) | undefined;
  /** Update selected entity ID */
  setSelectedEntityId: (entityId: string | undefined) => void;
  /** Update selected record ID */
  setSelectedRecordId: (recordId: string | undefined) => void;
  /** Update name of selected record */
  setSelectedRecordName: (recordName: string | undefined) => void;
  /** Update selected group ID */
  setSelectedGroupId: (groupId: string | undefined) => void;
  /** Mark a record as just created, or clear the mark with `undefined` */
  setNewRecordId: (recordId: string | undefined) => void;
}

export const SidebarControlContext = createContext<SidebarControlContextValue | null>(null);
